import { useState } from 'react';
import ResourceHeaderCard from './ResourceHeaderCard';
import {
  useResourcesInIncident,
  ResourceInIncident,
  useDeleteResourceInIncident,
} from 'components/IAP/hooks/resourceInIncidentHooks';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import CheckInDrawer from 'components/IAP/Resources/Drawers/CheckInDrawer';
import CheckOutDrawer from 'components/IAP/Resources/Drawers/CheckOutDrawer';
import { RESOURCE_STATUSES } from 'components/IAP/Resources/constants/resourceConstants';
import dayjs from 'dayjs';

const columns: {
  key: string;
  label: string;
}[] = [
  {
    key: 'checkin_datetime',
    label: 'Date/Time Check-in',
  },
  {
    key: 'resource_name',
    label: 'Resource Name',
  },
  {
    key: 'resource_kind',
    label: 'Kind',
  },
  {
    key: 'resource_type',
    label: 'Type',
  },
  {
    key: 'personnel_count',
    label: '# of Personnel',
  },
  {
    key: 'null',
    label: 'Actions',
  },
];

export const CheckedInResources: React.FC<{
  handleResourceUpdate: (updatedResource: Partial<ResourceInIncident>) => void;
  searchTerm: string;
}> = ({ handleResourceUpdate, searchTerm }) => {
  const [showCheckInDrawer, setShowCheckInDrawer] = useState(false);
  const [showCheckOutDrawer, setShowCheckOutDrawer] = useState(false);
  const [selectedResourceInIncident, setSelectedResourceInIncident] =
    useState<ResourceInIncident | null>(null);
  const { data: resourceInIncident } = useResourcesInIncident();
  const deleteResource = useDeleteResourceInIncident();

  return (
    <>
      <ResourceHeaderCard
        filters={{}}
        onFilterChange={() => {}}
        columns={columns}
        grid="checked-in-card-grid"
      />
      {resourceInIncident
        ?.filter(
          (res) =>
            (res.current_status === RESOURCE_STATUSES.AVAILABLE ||
              res.current_status === RESOURCE_STATUSES.IN_PROCESS) &&
            (!searchTerm ||
              (res.resource_name ?? '')
                .toLowerCase()
                .includes(searchTerm.toLowerCase()))
        )
        .map((resource) => (
          <CheckedInResourceRow
            key={resource.id}
            resource={resource}
            setSelectedResourceInIncident={setSelectedResourceInIncident}
            setShowCheckInDrawer={setShowCheckInDrawer}
            setShowCheckOutDrawer={setShowCheckOutDrawer}
            deleteResource={deleteResource}
          />
        ))}
      {showCheckInDrawer && selectedResourceInIncident && (
        <CheckInDrawer
          resource={selectedResourceInIncident}
          onClose={() => {
            setShowCheckInDrawer(false);
          }}
          onSave={(data) => {
            handleResourceUpdate({
              ...selectedResourceInIncident,
              ...data,
              current_status: RESOURCE_STATUSES.AVAILABLE,
              status_since: new Date().toISOString(),
            });
            setShowCheckInDrawer(false);
          }}
          isUpdate
        />
      )}
      {showCheckOutDrawer && selectedResourceInIncident && (
        <CheckOutDrawer
          resource={selectedResourceInIncident}
          onClose={() => {
            setShowCheckOutDrawer(false);
          }}
          onSave={(data: any) => {
            handleResourceUpdate({
              ...selectedResourceInIncident,
              ...data,
              current_status: RESOURCE_STATUSES.CHECKED_OUT,
            });
            setShowCheckOutDrawer(false);
          }}
        />
      )}
    </>
  );
};

const CheckedInResourceRow: React.FC<{
  resource: ResourceInIncident;
  setSelectedResourceInIncident: (resource: ResourceInIncident) => void;
  setShowCheckInDrawer: (show: boolean) => void;
  setShowCheckOutDrawer: (show: boolean) => void;
  deleteResource: any;
}> = ({
  resource,
  setSelectedResourceInIncident,
  setShowCheckInDrawer,
  setShowCheckOutDrawer,
  deleteResource,
}) => {
  return (
    <div className="checked-in-card-grid">
      <div></div>
      <div>{dayjs(resource.checkin_datetime).format('YYYY-MM-DD HH:mm')}</div>
      <div>{resource.resource_name}</div>
      <div>{resource.resource_kind}</div>
      <div>{resource.resource_type}</div>
      <div>{resource.personnel_count}</div>
      <div className="d-flex flex-nowrap gap-1">
        <StylishNewButton
          className="button--icon"
          onClick={() => {
            setSelectedResourceInIncident(resource);
            setShowCheckOutDrawer(true);
          }}
        >
          <SharedIcon iconName="exit_to_app" size="sm" />
        </StylishNewButton>
        <StylishNewButton
          className="button--icon"
          onClick={() => {
            setSelectedResourceInIncident(resource);
            setShowCheckInDrawer(true);
          }}
        >
          <SharedIcon iconName="edit" size="sm" />
        </StylishNewButton>
        <StylishNewButton
          className="button--icon"
          onClick={() => deleteResource.mutate(resource.id)}
        >
          <SharedIcon iconName="delete" size="sm" />
        </StylishNewButton>
      </div>
    </div>
  );
};
